/**
 * reportUi.js — the presentation layer the report views share.
 *
 * **It runs on the app's design system now, and it did not before.**
 * `theme.js` opens with "SINGLE SOURCE OF TRUTH. Every component imports from
 * here" — and this file carried its own palette: a navy `#345479` table header
 * that appears nowhere in the Warm Stone system, Georgia serif headings, and a
 * cream of its own. So the report pages were a second design system bolted
 * into the app, and they read like it. Brian, 21 September 2026, shown the
 * result twice: *"Its still shit. It needs to be better. This is an
 * institutional grade system, lets act like it."*
 *
 * Three things make a figures screen read as institutional, and none of them
 * is decoration:
 *
 * * **Every number is tabular and monospaced.** `theme.fontMono` with
 *   `font-variant-numeric: tabular-nums`, so a column of money lines up digit
 *   under digit and the eye can compare magnitudes without reading. A
 *   proportional font in a money column is the tell that a screen was never
 *   meant to be read as a document of record.
 * * **Colour is semantic only.** Ink for text, the theme's `danger` for a
 *   deficit, `warning` for a caution, `success` for a surplus. Nothing is
 *   coloured to decorate — the navy header bar was carrying no meaning at all,
 *   and it was the loudest thing on the page.
 * * **Rules are hairlines and headers are quiet.** A table header is muted
 *   uppercase over a single rule, not a filled bar; the data is what should
 *   carry weight.
 *
 * `C` keeps its name and its keys, mapped onto the theme, because the four
 * statements read a dozen of them — remapping in one place improves every
 * report page at once rather than the one being looked at (§0.21).
 *
 * The rule that runs through the formatting is the backend's own (§0.465,
 * §0.30): **a figure the book could not produce reads `n/a`, never `0.00`.**
 */

import t from "./theme";

/* ─── The palette, in the app's own tokens ───────────────────────── */
export const C = {
  bg: t.bgSubtle,
  card: t.bgCard,
  border: t.border,
  borderLight: t.borderLight,
  text: t.text,
  muted: t.textMuted,
  secondary: t.textSecondary,
  navy: t.text,          // was a second near-black; the theme has one
  blue: t.info,          // informational only, never a header fill
  cream: t.borderLight,  // used as a hairline, so it is a border token
  sand: t.border,
  green: t.success,
  red: t.danger,
  gold: t.warning,
  accent: t.accent,
  redBg: t.dangerBg,
  inputBg: t.bgInput,
  amber: t.warningBg,
  amberLine: t.warningBorder,
};

export const FONT = { body: t.fontBody, mono: t.fontMono, display: t.fontDisplay };

/* ─── Formatting. One spelling of "we could not say" ─────────────── */

export const nf = (digits) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/** A dollar figure, or `n/a` — never `0.00` for a missing conversion. */
export function usd(value, { cents = true } = {}) {
  if (value === null || value === undefined) return "n/a";
  const n = Number(value);
  if (Number.isNaN(n)) return "n/a";
  const body = nf(cents ? 2 : 0).format(Math.abs(n));
  return n < 0 ? `($${body})` : `$${body}`;
}

export function num(value, digits = 0) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return nf(digits).format(n);
}

export function when(value) {
  if (!value) return "—";
  return String(value);
}

/* ─── Type ───────────────────────────────────────────────────────── */

/** Every figure on a report wears this: monospaced and tabular, so a column
    of money lines up digit under digit. */
export const figure = {
  fontFamily: t.fontMono,
  fontVariantNumeric: "tabular-nums",
  letterSpacing: "-0.01em",
};

/** A section heading: quiet, letterspaced, over a hairline. */
export function SectionHead({ children, right }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap",
      borderBottom: `1px solid ${t.border}`, paddingBottom: 6, marginBottom: 12,
    }}>
      <h3 style={{
        margin: 0, fontFamily: t.fontBody, fontSize: 11, fontWeight: 700,
        letterSpacing: ".09em", textTransform: "uppercase", color: t.textSecondary,
      }}>
        {children}
      </h3>
      {right && <div style={{ marginLeft: "auto", display: "flex", gap: 14, alignItems: "baseline" }}>{right}</div>}
    </div>
  );
}

/** A caption under a heading — the sentence that says what a figure is. */
export function Note({ children }) {
  return (
    <p style={{
      margin: "0 0 12px", fontFamily: t.fontBody, fontSize: 12,
      lineHeight: 1.55, color: t.textMuted, maxWidth: "76ch",
    }}>
      {children}
    </p>
  );
}

export function Banner({ tone = "warn", title, children }) {
  const edge = tone === "bad" ? t.danger : tone === "good" ? t.success : t.warning;
  const fill = tone === "bad" ? t.dangerBg : tone === "good" ? t.successBg : t.warningBg;
  return (
    <div style={{
      background: fill, border: `1px solid ${edge}33`, borderLeft: `3px solid ${edge}`,
      borderRadius: t.radiusSm, padding: "10px 14px", marginBottom: 14,
      fontFamily: t.fontBody, fontSize: 12.5, lineHeight: 1.55, color: t.text,
    }}>
      {title && <strong>{title} </strong>}
      {children}
    </div>
  );
}

/** A stat. `scale="hero"` for the figure a screen is built around. */
export function Kpi({ name, value, hint, tone, scale = "stat" }) {
  const ink = tone === "bad" ? t.danger : tone === "good" ? t.success : t.text;
  const hero = scale === "hero";
  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.borderLight}`,
      borderTop: `2px solid ${tone === "bad" ? t.danger : tone === "good" ? t.success : t.border}`,
      borderRadius: t.radiusSm, padding: hero ? "14px 16px" : "12px 14px",
      flex: hero ? "1 1 240px" : "1 1 190px", minWidth: 180,
      boxShadow: t.shadowSm,
    }}>
      <div style={{
        fontFamily: t.fontBody, fontSize: 9.5, fontWeight: 700, color: t.textMuted,
        textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 6,
      }}>
        {name}
      </div>
      <div style={{ ...figure, fontSize: hero ? 27 : 21, fontWeight: 500, color: ink, lineHeight: 1.1 }}>
        {value}
      </div>
      {hint && (
        <div style={{
          fontFamily: t.fontBody, fontSize: 10.5, color: t.textMuted,
          marginTop: 7, lineHeight: 1.45,
        }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function KpiRow({ children }) {
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
      {children}
    </div>
  );
}

/** A checkbox that belongs to this design system rather than to the browser.
    The OS default is a saturated consumer blue and was the loudest thing on
    the page it sat in. */
export function Check({ checked, onChange, label }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      tabIndex={0}
      onClick={onChange}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChange(); }
      }}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 14, height: 14, borderRadius: 3, cursor: "pointer",
        border: `1px solid ${checked ? t.accent : t.border}`,
        background: checked ? t.accent : t.bgCard,
        color: t.textOnAccent, fontSize: 10, lineHeight: 1,
        transition: "background .12s, border-color .12s",
      }}
    >
      {checked ? "✓" : ""}
    </span>
  );
}

/* ─── Tables ─────────────────────────────────────────────────────── */

export const th = {
  textAlign: "left", padding: "0 10px 7px", fontFamily: t.fontBody,
  fontSize: 9.5, fontWeight: 700, color: t.textMuted,
  textTransform: "uppercase", letterSpacing: ".08em",
  background: t.bgCard, borderBottom: `1px solid ${t.border}`,
  position: "sticky", top: 0, whiteSpace: "nowrap",
};
export const thR = { ...th, textAlign: "right" };
export const td = {
  padding: "7px 10px", fontFamily: t.fontBody, fontSize: 12.5,
  borderBottom: `1px solid ${t.borderLight}`, color: t.text,
  verticalAlign: "top",
};
export const tdR = { ...td, ...figure, textAlign: "right" };

/** A table.

    `minWidth` is what forces the horizontal scroll on a narrow screen, and 760
    is right for the statements, whose columns are all figures. A table carrying
    a column of prose needs a smaller floor or the prose runs off the edge and
    the reader cannot see it — so it is a prop rather than a constant, and every
    existing caller keeps the number it was built with. */
export function Grid({ headers, children, minWidth = 760 }) {
  return (
    <div style={{
      overflowX: "auto", border: `1px solid ${t.borderLight}`,
      borderRadius: t.radiusSm, background: t.bgCard,
    }}>
      <table style={{
        width: "100%", borderCollapse: "collapse", minWidth,
        fontFamily: t.fontBody,
      }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={h.right ? thR : th}>{h.name ?? h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
